import React, { useCallback, useEffect, useState } from "react";

import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { Grid, xcss } from "@atlaskit/primitives";
import { media } from "@atlaskit/primitives/responsive";

import {
    WIZARD_PAGE_ONBOARDING_PROJECT_DETAILS_SOURCE_NAME,
    WIZARD_PAGE_ONBOARDING_WORK_TYPES_SOURCE_NAME,
    WIZARD_PAGE_ONBOARDING_BOARD_COLUMNS_SOURCE_NAME,
    ANALYTICS_PREFIX
} from "../../common/constants";
import { useWizardConfiguration } from "../../controllers";
import { IssueTypeWizardUI, type ProjectConfig } from "../../utils/types";
import { PageBoardColumns } from "../page-board-columns/page-board-columns";
import { PageProjectDetails } from "../page-project-details/page-project-details";
import { PageWorkTypes } from "../page-work-types/page-work-types";

const WIZARD_PAGES = [
    WIZARD_PAGE_ONBOARDING_PROJECT_DETAILS_SOURCE_NAME,
    WIZARD_PAGE_ONBOARDING_WORK_TYPES_SOURCE_NAME,
    WIZARD_PAGE_ONBOARDING_BOARD_COLUMNS_SOURCE_NAME
];

export const useAnalytics = () => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { teamType } = useWizardConfiguration();

    const fireAnalyticsEvent = useCallback(
        (eventData: Record<string, unknown>) => {
            createAnalyticsEvent(eventData).fire();
        },
        [createAnalyticsEvent]
    );

    return { fireAnalyticsEvent, teamType };
};

export const WelcomeModalPageSwitcher: React.FC<{
    onComplete: (projectConfig: ProjectConfig) => void;
}> = ({ onComplete }) => {
    const { formatMessage } = useIntl();
    const {
        wizardConfiguration,
        projectDetails: { template, defaultProjectName }
    } = useWizardConfiguration();
    const defaultColumns = wizardConfiguration.columns.map(
        (defaultColumnName) =>
            formatMessage({
                id: defaultColumnName.id,
                defaultMessage: defaultColumnName.defaultMessage
            })
    );
    const defaultIssueTypes = wizardConfiguration.issueTypes;
    const [currentPage, setCurrentPage] = useState(0);
    const [projectName, setProjectName] = useState<string | undefined>(
        undefined
    );
    const [columnNames, setColumnNames] = useState<string[]>(defaultColumns);
    const [selectedIssueTypes, setSelectedIssueTypes] = useState<
        IssueTypeWizardUI[]
    >(defaultIssueTypes?.filter((issue) => issue.isPreSelected) || []);

    const analytics = useAnalytics();

    const { fireAnalyticsEvent, teamType } = analytics;

    const onToggleIssueType = useCallback(
        (issueType: IssueTypeWizardUI) => {
            setSelectedIssueTypes((prevTypes) =>
                prevTypes.includes(issueType)
                    ? prevTypes.filter((type) => type !== issueType)
                    : [...prevTypes, issueType]
            );

            fireAnalyticsEvent({
                action: "clicked",
                actionSubject: "button",
                actionSubjectId: `${ANALYTICS_PREFIX}WorkTypesButton`,
                eventType: "ui",
                attributes: {
                    issueTypeClicked: issueType,
                    selectedIssueTypes,
                    teamType,
                    template
                }
            });
        },
        [
            setSelectedIssueTypes,
            fireAnalyticsEvent,
            selectedIssueTypes,
            teamType,
            template
        ]
    );

    const onFinishHandler = useCallback(() => {
        const hasChangedDefaultIssueTypeConfiguration = !(
            defaultIssueTypes.length === selectedIssueTypes.length &&
            defaultIssueTypes.every((defaultType) =>
                selectedIssueTypes.some(
                    (selectedType) => selectedType.id === defaultType.id
                )
            )
        );

        const selectedIssuesInPriorityOrder = defaultIssueTypes
            .map((defaultType) =>
                selectedIssueTypes.find(
                    (selectedType) => selectedType.id === defaultType.id
                )
            )
            .filter(
                (issueType): issueType is IssueTypeWizardUI =>
                    issueType !== undefined
            );

        fireAnalyticsEvent({
            action: "completed",
            actionSubject: `${ANALYTICS_PREFIX}Setup`,
            eventType: "track",
            attributes: {
                totalSelected: selectedIssueTypes.length,
                totalColumns: columnNames.length,
                defaultIssueTypes,
                selectedIssueTypes: selectedIssuesInPriorityOrder,
                hasChangedDefaultIssueTypeConfiguration,
                hasChangedDefaultProjectName: projectName !== undefined,
                teamType,
                template
            }
        });

        onComplete({
            projectName:
                projectName === undefined
                    ? formatMessage(defaultProjectName)
                    : projectName,
            issueTypes: selectedIssuesInPriorityOrder.map(
                ({ icon, avatarId }) => ({
                    name: icon,
                    avatarId
                })
            ),
            columns: columnNames
        });
    }, [
        defaultIssueTypes,
        selectedIssueTypes,
        fireAnalyticsEvent,
        columnNames,
        projectName,
        teamType,
        template,
        onComplete,
        formatMessage,
        defaultProjectName
    ]);

    const setNextWizardPage = useCallback(() => {
        if (
            WIZARD_PAGES[currentPage] ===
            WIZARD_PAGE_ONBOARDING_BOARD_COLUMNS_SOURCE_NAME
        ) {
            fireAnalyticsEvent({
                action: "clicked",
                actionSubject: "button",
                actionSubjectId: `${ANALYTICS_PREFIX}FinishButton`,
                eventType: "ui",
                attributes: {
                    teamType,
                    template
                }
            });
            onFinishHandler();
        } else {
            fireAnalyticsEvent({
                action: "clicked",
                actionSubject: "button",
                actionSubjectId: `${ANALYTICS_PREFIX}NextButton`,
                eventType: "ui",
                attributes: {
                    currentPageBeforeNextClick: WIZARD_PAGES[currentPage],
                    teamType,
                    template
                }
            });
            setCurrentPage(currentPage + 1);
        }
    }, [
        currentPage,
        fireAnalyticsEvent,
        onFinishHandler,
        setCurrentPage,
        teamType,
        template
    ]);

    const defaultProjectNameFormatted = formatMessage(defaultProjectName);

    const handleProjectNameChange = useCallback(
        (newProjectName: string) => {
            setProjectName(newProjectName);
        },
        [setProjectName]
    );

    const wizardPage = WIZARD_PAGES[currentPage] || WIZARD_PAGES[0];

    switch (wizardPage) {
        case WIZARD_PAGE_ONBOARDING_PROJECT_DETAILS_SOURCE_NAME:
        default:
            return (
                <Grid gap="space.0" xcss={gridStyles}>
                    <PageProjectDetails
                        onClickNext={setNextWizardPage}
                        currentProjectName={projectName}
                        onProjectNameChange={handleProjectNameChange}
                        defaultProjectName={defaultProjectNameFormatted}
                    />
                </Grid>
            );
        case WIZARD_PAGE_ONBOARDING_WORK_TYPES_SOURCE_NAME:
            return (
                <Grid gap="space.0" xcss={gridStyles}>
                    <PageWorkTypes
                        defaultIssueTypes={defaultIssueTypes}
                        projectName={projectName ?? defaultProjectNameFormatted}
                        onClickNext={setNextWizardPage}
                        selectedIssueTypes={selectedIssueTypes}
                        onToggleIssueType={onToggleIssueType}
                    />
                </Grid>
            );
        case WIZARD_PAGE_ONBOARDING_BOARD_COLUMNS_SOURCE_NAME:
            return (
                <Grid gap="space.0" xcss={gridStyles}>
                    <PageBoardColumns
                        onClickNext={setNextWizardPage}
                        projectName={projectName ?? defaultProjectNameFormatted}
                        columnNames={columnNames}
                        setColumnNames={setColumnNames}
                    />
                </Grid>
            );
    }
};

const gridStyles = xcss({
    width: "100%",
    height: "100%",
    gridTemplateColumns: "100%",
    [media.above.sm]: {
        gridTemplateColumns: "50% 50%"
    }
});
